import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../api/context';
import { ApiError } from '../api/client';
import { IconButton, Button } from '../design/primitives/Button';
import { Popover } from '../design/primitives/Overlay';
import { EmptyState } from '../design/primitives/Misc';
import { Icon } from '../design/icons';
import { formatDateTime } from '../../lib/format';
import { useDataSource } from '../meta/useDataSource';

// Notification tray: /v1/notifications (blueprint §5.3). Polls every minute while the tab is
// visible; unread count on the bell; click marks read (POST /notifications/:id/read) + follows the
// link. Absent backend → the bell simply shows nothing. On a connected system (Halo) the feed is the
// agent's Halo notifications and read state is Halo's: nothing is marked read from here.

export interface Notification {
  id: string;
  kind: string;
  title: string;
  body?: string | null;
  link?: string | null;
  read_at?: string | null;
  created_at: string;
}

export function NotificationTray() {
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const key = ['notifications'];
  // Read state belongs to the connected system when there is one (useDataSource).
  const canMarkRead = useDataSource().overlay === null;
  const q = useQuery<Notification[]>({
    queryKey: key,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    retry: false,
    staleTime: 30_000,
    queryFn: async () => {
      try {
        const page = await api.resource<Notification>('notifications').list({ sort: '-created_at', limit: 30 });
        return page.data;
      } catch (e) {
        if (e instanceof ApiError && (e.isNotFound || e.isNetwork || e.status === 501)) return [];
        throw e;
      }
    },
  });
  const markRead = useMutation({
    mutationFn: (id: string | 'all') => (id === 'all' ? api.resource('notifications').action('read-all') : api.resource('notifications').action('read', { id })),
    onSettled: () => void qc.invalidateQueries({ queryKey: key }),
  });
  const list = q.data ?? [];
  const unread = list.filter((n) => !n.read_at).length;

  return (
    <>
      <span className="hf-bell-wrap">
        <IconButton ref={ref} icon={unread ? 'bell' : 'bell'} label={unread ? `${unread} unread notifications` : 'Notifications'} size="sm" aria-expanded={open} aria-haspopup="dialog" onClick={() => setOpen((v) => !v)} />
        {unread > 0 && <span className="hf-bell-dot" aria-hidden>{unread > 9 ? '9+' : unread}</span>}
      </span>
      <Popover open={open} onClose={() => setOpen(false)} anchor={ref} placement="bottom-end" aria-label="Notifications" trapFocus className="hf-tray">
        <div className="hf-tray-head">
          <b>Notifications</b>
          {unread > 0 && canMarkRead && (
            <Button size="sm" variant="link" onClick={() => markRead.mutate('all')}>
              Mark all read
            </Button>
          )}
        </div>
        <div className="hf-tray-list hf-scroll">
          {list.length === 0 ? (
            <EmptyState title="You're all caught up" icon="bell-off" />
          ) : (
            list.map((n) => (
              <button
                key={n.id}
                type="button"
                className={['hf-tray-item', !n.read_at && 'unread'].filter(Boolean).join(' ')}
                onClick={() => {
                  if (!n.read_at && canMarkRead) markRead.mutate(n.id);
                  setOpen(false);
                  if (n.link) navigate(n.link);
                }}
              >
                <span className="dot" aria-hidden />
                <span className="hf-grow" style={{ minWidth: 0 }}>
                  <span className="t">{n.title}</span>
                  {n.body && <span className="b">{n.body}</span>}
                  <span className="hf-muted hf-num when">{formatDateTime(n.created_at)}</span>
                </span>
                {n.link && <Icon name="chevron-right" size={13} className="hf-muted" />}
              </button>
            ))
          )}
        </div>
      </Popover>
    </>
  );
}
